import React from "react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ds";

/**
 * Point-of-opt-in SMS consent disclosure (A2P 10DLC / CTIA).
 *
 * Carrier review requires the disclosure to appear where consent is actually given — not only in
 * the Terms or the footer — and requires that it is not pre-checked or bundled with account
 * creation. Marketing consent is therefore a SEPARATE, optional checkbox (`showMarketing`), never
 * combined with the transactional consent above it.
 */
export const SMS_CONSENT_TEXT =
  "By checking this box, you agree to receive recurring SMS messages related to your inquiries, " +
  "appointments, scheduling, service updates, and other applicable communications. Message " +
  "frequency varies. Message and data rates may apply. Reply STOP to opt out and HELP for help. " +
  "Consent is not a condition of purchase.";

export const SMS_MARKETING_TEXT =
  "Optional: I also agree to receive promotional and marketing SMS messages. Message frequency " +
  "varies. Message and data rates may apply. Reply STOP to opt out and HELP for help. Consent is " +
  "not a condition of purchase.";

const LegalLinks: React.FC = () => (
  <>
    See our{" "}
    <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 underline hover:text-blue-700">
      Terms &amp; Conditions
    </Link>{" "}
    and{" "}
    <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 underline hover:text-blue-700">
      Privacy Policy
    </Link>
    .
  </>
);

export interface SmsConsentProps {
  /** Transactional/informational consent. Must default to false — never pre-checked. */
  checked: boolean;
  onChange: (v: boolean) => void;
  /** Render the separate, optional marketing-consent checkbox. */
  showMarketing?: boolean;
  marketingChecked?: boolean;
  onMarketingChange?: (v: boolean) => void;
}

export const SmsConsent: React.FC<SmsConsentProps> = ({
  checked,
  onChange,
  showMarketing = false,
  marketingChecked = false,
  onMarketingChange,
}) => (
  <div className="mt-6 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="sms-consent">
    <label className="flex cursor-pointer items-start gap-3">
      <span className="mt-0.5 shrink-0">
        <Checkbox checked={checked} onChange={onChange} testid="sms-consent-checkbox" />
      </span>
      <span className="text-xs leading-5 text-slate-600">
        {SMS_CONSENT_TEXT} <LegalLinks />
      </span>
    </label>

    {showMarketing && (
      <label className="flex cursor-pointer items-start gap-3 border-t border-slate-200 pt-3">
        <span className="mt-0.5 shrink-0">
          <Checkbox
            checked={marketingChecked}
            onChange={(v) => onMarketingChange?.(v)}
            testid="sms-marketing-consent-checkbox"
          />
        </span>
        <span className="text-xs leading-5 text-slate-600">
          {SMS_MARKETING_TEXT} <LegalLinks />
        </span>
      </label>
    )}
  </div>
);

export default SmsConsent;
